import { message } from 'antd'
import { getErrorCode } from './errorCode'
import { logger } from './logger'
import i18n from './i18n'

/**
 * Map known environmental Firebase error codes to a user-facing message.
 * Returns null for unrecognized codes, so the caller uses the generic
 * fallback. Only codes that are *not actionable bugs* but where
 * the user benefits from knowing what actually happened.
 */
function getEnvironmentalErrorMessage(error: unknown): string | null {
  // Some backend HttpsErrors carry a `details.reason` discriminator whose
  // message is already user-facing German: the preview's storage object
  // (preview-file-missing) or Firestore record (preview-not-found) was already
  // imported or deleted while the operator's list was stale.
  const details =
    error && typeof error === 'object' && 'details' in error
      ? (error as { details?: { reason?: unknown } }).details
      : undefined
  if (
    details?.reason === 'preview-file-missing' ||
    details?.reason === 'preview-not-found'
  ) {
    const message = (error as { message?: unknown }).message
    return typeof message === 'string' && message.length > 0 ? message : null
  }

  const code = getErrorCode(error)
  if (!code) return null

  switch (code) {
    case 'storage/upload-stalled':
      // Our own watchdog in attachmentUploads.ts aborted a staging upload that
      // stopped transferring bytes. The operator's message and attachment are
      // both kept, so the message must not imply data loss.
      return i18n.t('frontend:chatwoot.attachment.uploadStalled')
    case 'storage/retry-limit-exceeded':
      // Firebase Storage gave up after exponential-backoff retries: the
      // user's network is too slow or unstable.
      return i18n.t('frontend:common.errors.connectionTooSlow')
    case 'functions/deadline-exceeded':
      // The callable hit its client-side deadline (about 70s by default; long
      // methods raise it per call). Still captured to Sentry, but beforeSend
      // (src/index.jsx) groups these into one message-only issue because they
      // are network conditions, not per-method bugs.
      return i18n.t('frontend:common.errors.connectionTooSlow')
    case 'auth/operation-not-allowed':
      // Firebase's SMS region policy blocks the destination country, so the
      // 2FA SMS was never sent. Fixed by adding the country to the allowlist
      // (Authentication > Settings > SMS region policy). Still captured to
      // Sentry so new countries surface.
      return i18n.t('frontend:common.errors.smsRegionNotSupported')
    default:
      return null
  }
}

/**
 * Backend `HttpsError`s whose *message* is a known, expected business-condition
 * rejection (a duplicate, or a stale-UI / double-submit race the server
 * correctly rejected) rather than an actionable bug. We surface a specific,
 * localized message and skip Sentry capture — the same treatment
 * `functions/unauthenticated` gets below. Keyed by the message string the
 * backend throws, which the client `FirebaseError` surfaces verbatim. Any
 * message not listed here still flows through to capture as a real error.
 */
const onboardingFileDeleteLinkRequired =
  'frontend:onboarding.fileDeleteLinkRequired'

const expectedBusinessConditionMessages: Record<string, string> = {
  ABSENCE_OVERLAP: 'frontend:absences.modal.duplicateAbsenceError',
  CORRECTION_NOT_POSSIBLE:
    'frontend:absences.corrections.problems.CORRECTION_NOT_POSSIBLE',
  CORRECTION_PENDING:
    'frontend:absences.corrections.problems.CORRECTION_PENDING',
  CORRECTION_UNCHANGED:
    'frontend:absences.corrections.problems.CORRECTION_UNCHANGED',
  CORRECTION_NOT_PENDING:
    'frontend:absences.corrections.problems.CORRECTION_NOT_PENDING',
  CORRECTION_STATE_CHANGED:
    'frontend:absences.corrections.problems.CORRECTION_STATE_CHANGED',
  BEFORE_PAYROLL_START:
    'frontend:oneTimePayments.modal.blockedBeforePayrollStartError',
  // Backend security guard: users with an unverified email cannot be added as
  // company employers or tax advisor managers.
  USER_EMAIL_NOT_VERIFIED: 'frontend:common.errors.userEmailNotVerified',
  // Defensive net only: the backend dismiss is now idempotent, so this is a
  // fallback for older clients or a concurrent delete racing the dismiss.
  'Splitting job not found':
    'frontend:operations.documentUpload.splittingJobAlreadyDismissed',
  'company-sync-disabled': 'frontend:apps.sync.disabledForCompany',
  // The picker's own eligibility list can go stale between load and click (an
  // employment-type edit lands while the certificates page is already open),
  // so the server-side getExitEligibility check is the one that actually
  // fires here. Same wording the exit-capture flow already shows for this
  // exact condition.
  'certificate-not-available-for-job-type':
    'frontend:certificateCases.exit.recordedNoCase',
  'trial-settlement-already-approved':
    'frontend:trialSettlement.alreadyApproved',
  'trial-settlement-already-responded':
    'frontend:trialSettlement.alreadyResponded',
  'first-trial-settlement-not-billable':
    'frontend:trialSettlement.firstNotBillable',
  'DATEV Beraternummer and Mandantennummer must be numeric when DATEV is enabled':
    'frontend:company.datevNumbersNotNumeric',
  // Outbound Chatwoot attachments the backend refused on size. The operator
  // picked a file the client-side caps should have caught (or a stale tab
  // predating them), so this is a user-correctable condition, not a bug worth a
  // Sentry event — surface the same wording the picker uses.
  'Attachment exceeds the maximum allowed size':
    'frontend:chatwoot.attachment.tooLargeServer',
  'Attachments exceed the maximum total size':
    'frontend:chatwoot.attachment.totalTooLarge',
  // The staged object vanished between upload and forward (a second tab sending
  // the same draft, or a cleanup racing a retry). Expected, and the operator
  // just needs to re-attach.
  'Attachment was not found': 'frontend:chatwoot.attachment.notFound',
  'Session token is required for unauthenticated file deletion':
    onboardingFileDeleteLinkRequired,
  'No session token found for this onboarding':
    onboardingFileDeleteLinkRequired,
  'Invalid session token': onboardingFileDeleteLinkRequired,
  'onboarding-not-enabled': 'frontend:companyOnboarding.errors.notEnabled',
  'company-is-demo': 'frontend:companyOnboarding.errors.companyIsDemo',
  'company-has-closed-month':
    'frontend:companyOnboarding.errors.companyHasClosedMonth',
  'onboarding-already-started':
    'frontend:companyOnboarding.errors.alreadyStarted',
  'onboarding-not-found': 'frontend:companyOnboarding.errors.notFound',
  'onboarding-setup-state-changed':
    'frontend:companyOnboarding.errors.setupStateChanged',
  'onboarding-setup-already-completed':
    'frontend:companyOnboarding.errors.setupAlreadyCompleted',
  'onboarding-not-awaiting-data':
    'frontend:companyOnboarding.errors.notAwaitingData',
  'onboarding-invalid-open-answer':
    'frontend:companyOnboarding.errors.invalidOpenAnswer',
  'onboarding-data-in-review': 'frontend:companyOnboarding.errors.dataInReview',
  'onboarding-not-in-review': 'frontend:companyOnboarding.errors.notInReview',
  'onboarding-comment-required':
    'frontend:companyOnboarding.errors.commentRequired',
  'onboarding-setup-move-not-allowed':
    'frontend:companyOnboarding.errors.setupMoveNotAllowed',
}

// The localized message for an expected business condition, or null when the
// error isn't one. Consulted by BOTH getLocalizedErrorMessage and errorResponse:
// callers that pre-resolve their message (the Chatwoot composers) pass the
// result back in as `messageToUser`, which wins over the table lookup below — so
// without this the specific text would never reach the operator.
function getBusinessConditionMessage(error: unknown): string | null {
  const key =
    error &&
    typeof error === 'object' &&
    typeof (error as { message?: unknown }).message === 'string'
      ? expectedBusinessConditionMessages[
          (error as { message: string }).message
        ]
      : undefined
  return key ? i18n.t(key) : null
}

// Absence-validation rules that refuse a save (A1/A2/D3) send their check ids
// in `details.checkIds`. The schema-level `VALIDATION_ERROR` from
// validateAbsence() sends none and stays a reportable error.
function getAbsenceValidationCheckIds(error: unknown): string[] | null {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? (error as { message?: unknown }).message
      : undefined
  if (message !== 'VALIDATION_ERROR') return null

  const details =
    error && typeof error === 'object' && 'details' in error
      ? (error as { details?: { checkIds?: unknown } }).details
      : undefined
  return Array.isArray(details?.checkIds)
    ? [
        ...new Set(
          details.checkIds.filter(
            (id): id is string => typeof id === 'string' && id.length > 0
          )
        ),
      ]
    : []
}

function formatAbsenceValidationBlockedMessage(checkIds: string[]): string {
  if (checkIds.length === 0) {
    return i18n.t('frontend:absences.modal.validationBlockedError')
  }

  const reasons = checkIds
    .map((checkId) => i18n.t(`frontend:validation.absence.${checkId}`))
    .join(' ')
  return `${i18n.t('frontend:absences.modal.validationBlockedErrorPrefix')} ${reasons}`
}

function getAbsenceValidationBlockedMessage(error: unknown): string | null {
  const checkIds = getAbsenceValidationCheckIds(error)
  return checkIds == null
    ? null
    : formatAbsenceValidationBlockedMessage(checkIds)
}

/**
 * Translate a thrown callable/storage error into a localized, user-facing
 * message. Backend `HttpsError`s carry hardcoded English messages that the
 * client `FirebaseError.message` surfaces verbatim, so this maps known error
 * `code`s to translated strings and otherwise returns the caller-provided
 * `fallback`. It never returns the raw backend message.
 */
export function getLocalizedErrorMessage(
  error: unknown,
  fallback: string
): string {
  const environmental = getEnvironmentalErrorMessage(error)
  if (environmental) return environmental

  const businessCondition = getBusinessConditionMessage(error)
  if (businessCondition) return businessCondition

  const absenceValidationBlocked = getAbsenceValidationBlockedMessage(error)
  if (absenceValidationBlocked) return absenceValidationBlocked

  switch (getErrorCode(error)) {
    case 'functions/permission-denied':
    case 'storage/unauthorized':
      return i18n.t('frontend:common.errors.permissionDenied')
    case 'functions/unauthenticated':
      return i18n.t('frontend:common.errors.sessionExpired')
    default:
      return fallback
  }
}

/**
 * Structured detail an error class attaches for Sentry. Tags are indexed and
 * must stay low-cardinality, so measurements (byte counts, sizes) belong here
 * instead. Never put personal data in one — it lands in Sentry verbatim.
 */
function getSentryContext(error: unknown): Record<string, unknown> | undefined {
  const context =
    error && typeof error === 'object' && 'sentryContext' in error
      ? (error as { sentryContext?: unknown }).sentryContext
      : undefined
  return context && typeof context === 'object' && !Array.isArray(context)
    ? (context as Record<string, unknown>)
    : undefined
}

export function errorResponse(
  error: any,
  messageToUser?: string,
  tags?: Record<string, string>
) {
  // `unauthenticated` means the Firebase Auth token was missing or expired
  // when the call ran (a token-refresh race, or Safari ITP evicting the
  // session). Not an actionable bug, so show "session expired" and skip Sentry.
  if (getErrorCode(error) === 'functions/unauthenticated') {
    // noinspection JSIgnoredPromiseFromCall
    message.error(i18n.t('frontend:common.errors.sessionExpired'))
    return
  }

  // Expected, handled business-condition rejection (a duplicate, or a stale-UI
  // race the server correctly rejected). Show its specific localized message
  // and skip Sentry capture. A caller-provided `messageToUser` still wins.
  const businessConditionMessage = getBusinessConditionMessage(error)
  if (businessConditionMessage) {
    // noinspection JSIgnoredPromiseFromCall
    message.error(messageToUser ?? businessConditionMessage)
    return
  }

  // An absence save refused by a blocking validation rule: expected and
  // user-correctable, so show the finding text and skip Sentry capture.
  const absenceValidationCheckIds = getAbsenceValidationCheckIds(error)
  if (absenceValidationCheckIds?.length) {
    // noinspection JSIgnoredPromiseFromCall
    message.error(
      messageToUser ??
        formatAbsenceValidationBlockedMessage(absenceValidationCheckIds)
    )
    return
  }

  // Expected stale-list rejection from assignPreviews / overrideSepaCheck: the
  // preview's storage object (preview-file-missing) or Firestore record
  // (preview-not-found) was already imported or deleted when the operator
  // acted on it. The backend's message is already user-facing, and the server
  // correctly rejected the action, so skip Sentry capture.
  const details =
    error && typeof error === 'object' && 'details' in error
      ? (error as { details?: { reason?: unknown } }).details
      : undefined
  if (
    details?.reason === 'preview-file-missing' ||
    details?.reason === 'preview-not-found'
  ) {
    // noinspection JSIgnoredPromiseFromCall
    message.error(
      messageToUser ??
        getEnvironmentalErrorMessage(error) ??
        i18n.t('frontend:common.errors.generic')
    )
    return
  }

  const finalMessage =
    messageToUser ??
    getEnvironmentalErrorMessage(error) ??
    i18n.t('frontend:common.errors.generic')
  logger.captureException(
    error,
    { ...getSentryContext(error), userMessage: finalMessage },
    tags
  )
  // noinspection JSIgnoredPromiseFromCall
  message.error(finalMessage)
}
